/**
 * C2 — type-safe screen IDs per scenario.
 *
 * Centralizes the list of valid screen IDs each scenario uses, so:
 *
 *   • SceneRouter's switch statements can be type-narrowed against a
 *     union, catching renumbers at compile time.
 *   • Helper arrays like T2_TRACE_ARC derive from the source-of-truth
 *     list rather than carrying duplicate magic strings.
 *   • Scenario-JSON authoring errors (typo'd screen IDs) become
 *     compile errors when SceneRouter is later wired through the
 *     registry (M1).
 *
 * The lists must match `scenarios/<id>.json`'s `screenCheckpoints`
 * EXACTLY. The backend validator (Q3) enforces uniqueness + monotonic
 * offsets on the JSON side; this file enforces TypeScript-side
 * existence.
 *
 * When you add/remove a screen, change BOTH the scenario JSON and the
 * matching tuple here. Mismatch produces:
 *   • TypeScript error if SceneRouter is fully wired through the
 *     registry (M1).
 *   • Runtime fallthrough if not — but the visible bug surfaces fast.
 */

/* eslint-disable @typescript-eslint/no-unused-vars */

// ---------------------------------------------------------------------------
// T1 — onboarding (post-N4 cut: 0.x is 4 screens, not 6).
//
// Note: 0.2 + 0.4 legacy IDs remain in SceneRouter as dead routes for
// manual /control/seek compatibility; they are NOT in the scenario JSON
// or in this tuple.

// Per-act tuples — give each act-N renderer file its own exhaustive
// Record key, so missing-screen errors are local to the act being
// authored. T1_SCREENS below unions all four together for the
// JSON-mirroring test.

export const ACT0_SCREENS = ["0.1", "0.3", "0.5", "0.6"] as const;
export type Act0Screen = typeof ACT0_SCREENS[number];

export const ACT1_SCREENS = ["1.1", "1.2", "1.3", "1.4", "1.5", "1.6", "1.7", "1.8"] as const;
export type Act1Screen = typeof ACT1_SCREENS[number];

export const ACT2_SCREENS = ["2.1", "2.2", "2.3", "2.4", "2.4b", "2.5", "2.6", "2.7"] as const;
export type Act2Screen = typeof ACT2_SCREENS[number];

export const ACT3_SCREENS = ["3.1", "3.2", "3.3", "3.4", "3.5", "3.6", "3.7"] as const;
export type Act3Screen = typeof ACT3_SCREENS[number];

export const T1_SCREENS = [
  ...ACT0_SCREENS, ...ACT1_SCREENS, ...ACT2_SCREENS, ...ACT3_SCREENS,
] as const;
export type T1Screen = typeof T1_SCREENS[number];

// Act-classifiers for SceneRouter dispatch.
export function isAct0Screen(s: string): s is Act0Screen {
  return (ACT0_SCREENS as ReadonlyArray<string>).includes(s);
}
export function isAct1Screen(s: string): s is Act1Screen {
  return (ACT1_SCREENS as ReadonlyArray<string>).includes(s);
}
export function isAct2Screen(s: string): s is Act2Screen {
  return (ACT2_SCREENS as ReadonlyArray<string>).includes(s);
}
export function isAct3Screen(s: string): s is Act3Screen {
  return (ACT3_SCREENS as ReadonlyArray<string>).includes(s);
}

// ---------------------------------------------------------------------------
// T2 — bank-financed repeat trade (post-renumber: shipment + cert anchor
// live AFTER LC issuance at 4.14 + 4.15).

export const T2_SCREENS = [
  "4.1",  // Welcome back
  "4.2",  // Reorder draft
  "4.3",  // Counter-offer + accept
  "4.4",  // Logistics + Insurance picker
  "4.5",  // Contract capture + sign-CTA gate + Begin LC composition CTA
  "4.6",  // Document carousel (poultry)
  "4.7",  // Reconciliation 6×6
  "4.8",  // Invoice FSM + sign-CTA gate
  "4.9",  // Compliance snapshot
  "4.10", // Tariff estimate at LC composition
  "4.11", // LC composer (LC bundle package)
  "4.12", // Gate cascade
  "4.13", // LC issued + advising bank
  "4.14", // SmartLogistics (shipment AFTER LC)
  "4.15", // Insurance cert anchor (view-cert button)
  "4.16", // Tariff final + settlement
  "4.17", // T2 reveal
  "4.18", // Bridge B
] as const;
export type T2Screen = typeof T2_SCREENS[number];

// The T2 trace-pillar screens — non-contiguous post-renumber because
// shipment + cert anchor sit after the LC issuance arc. Derived directly
// from T2_SCREENS so it stays in sync if screen IDs ever shift.
export const T2_TRACE_ARC: ReadonlyArray<T2Screen> = ["4.6", "4.7", "4.8", "4.9", "4.14", "4.15"];

// ---------------------------------------------------------------------------
// T3 — reverse corridor (AE → BR, urea fertilizer, Itaú full LC).

export const T3_SCREENS = [
  "5.1",  // Pampagro RFQ
  "5.2",  // Gulf Phosphate reply
  "5.3",  // Reverse-corridor discovery
  "5.4",  // Logistics + Insurance picker (FOB · Pampagro arranges)
  "5.5",  // Contract capture
  "5.6",  // Document carousel (fertilizer)
  "5.7",  // Reconciliation
  "5.8",  // Invoice FSM
  "5.9",  // Cross-border compliance snapshot (BR-side)
  "5.10", // Tariff estimate at LC composition
  "5.11", // LC composer (100% Itaú)
  "5.12", // Gate cascade
  "5.13", // LC issued
  "5.14", // SmartLogistics (after LC issuance — matches T2 4.14)
  "5.15", // Insurance cert anchor (FOB · Itaú loss-payee · post-shipment)
  "5.16", // Tariff final on import (BR)
  "5.17", // Settlement (BRL ↔ AED)
  "5.18", // T3 reveal
] as const;
export type T3Screen = typeof T3_SCREENS[number];

export const T3_TRACE_ARC: ReadonlyArray<T3Screen> = ["5.6", "5.7", "5.8", "5.9", "5.14", "5.15"];

// ---------------------------------------------------------------------------
// T4 — air-cargo trade (BR → UAE chilled halal beef tenderloin, Qatar
// Airways Cargo, Verde Carnes Brasil ↔ Al Bayt Premium Meats, FAB
// fast-track 7-day LC). Mirrors T3's 18-screen shape; act 7 to keep the
// numbering monotonic (act 6 is the final-reveal scenario).

export const T4_SCREENS = [
  "7.1",  // Al Bayt RFQ
  "7.2",  // Verde Carnes reply
  "7.3",  // Air-corridor discovery
  "7.4",  // Logistics + Insurance picker (CIP · Verde Carnes arranges · Qatar Airways Cargo)
  "7.5",  // Contract capture
  "7.6",  // Document carousel (meat-air set)
  "7.7",  // Reconciliation (AWB ↔ Invoice piece-count discrepancy auto-resolves)
  "7.8",  // Invoice FSM (Verde Carnes Corp DID)
  "7.9",  // SmartLogistics (air modality · VCP → DOH → DXB)
  "7.10", // Insurance cert anchor (CIP · mandatory)
  "7.11", // Cross-border compliance snapshot (DXB Cargo Customs)
  "7.12", // Tariff estimate at LC composition
  "7.13", // LC composer (7-day fast-track tenor)
  "7.14", // Gate cascade + placement fee
  "7.15", // LC issued (FAB, fast-track) + success popup
  "7.16", // Tariff final at clearance
  "7.17", // Settlement (BRL ↔ AED, fast cycle)
  "7.18", // T4 reveal
] as const;
export type T4Screen = typeof T4_SCREENS[number];

export const T4_TRACE_ARC: ReadonlyArray<T4Screen> = ["7.6", "7.7", "7.8", "7.9", "7.10", "7.11"];

// ---------------------------------------------------------------------------
// All screen IDs across all scenarios — union of all four.

// Act 6 — final-reveal scenario (single screen).
export const ACT6_SCREENS = ["6.1"] as const;
export type Act6Screen = typeof ACT6_SCREENS[number];
export function isAct6Screen(s: string): s is Act6Screen {
  return (ACT6_SCREENS as ReadonlyArray<string>).includes(s);
}

export type AnyScreen = T1Screen | T2Screen | T3Screen | T4Screen | Act6Screen;

export const ALL_SCREENS: ReadonlyArray<AnyScreen> = [
  ...T1_SCREENS,
  ...T2_SCREENS,
  ...T3_SCREENS,
  ...T4_SCREENS,
  ...ACT6_SCREENS,
];

/**
 * Type guard. `isT2Screen(s)` narrows `s: string` to `T2Screen`.
 * Used by `pickCounterpart` etc. to validate screen IDs at runtime.
 */
export function isT2Screen(s: string): s is T2Screen {
  return (T2_SCREENS as ReadonlyArray<string>).includes(s);
}

export function isT3Screen(s: string): s is T3Screen {
  return (T3_SCREENS as ReadonlyArray<string>).includes(s);
}

export function isT4Screen(s: string): s is T4Screen {
  return (T4_SCREENS as ReadonlyArray<string>).includes(s);
}

export function isT1Screen(s: string): s is T1Screen {
  return (T1_SCREENS as ReadonlyArray<string>).includes(s);
}
